import { faChevronLeft } from "@jewlr/pro-light-svg-icons/faChevronLeft"
import { faChevronRight } from "@jewlr/pro-light-svg-icons/faChevronRight"
import { faCircle } from "@jewlr/pro-solid-svg-icons/faCircle"
import useEmblaCarousel from "embla-carousel-react"
import PropTypes from "prop-types"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import { cn } from "styles/utils"

import Button from "./button"
import FontAwesomeIcon from "./font-awesome-icon"

const CarouselContext = createContext(null)

const useCarousel = () => {
  const context = useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

const Carousel = ({
  children,
  className,
  description,
  disableSkip = false,
  opts,
  plugins,
  role,
  setApi,
  ...props
}) => {
  const [carouselRef, api] = useEmblaCarousel(
    {
      align: "start",
      loop: true,
      ...opts,
    },
    plugins
  )

  const skippedRef = useRef()
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback((api) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
    setSelectedIndex(api.selectedScrollSnap)
  }, [])

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api])
  const scrollNext = useCallback(() => api?.scrollNext(), [api])
  const scrollTo = useCallback((index) => api?.scrollTo(index), [api])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        scrollPrev()
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext]
  )

  const handleSkipCarousel = useCallback((e) => {
    skippedRef.current?.focus()
    e.preventDefault()
    e.stopPropagation()
  }, [])

  useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on("reInit", onSelect)
    api.on("select", onSelect)

    return () => api?.off("select", onSelect)
  }, [api, onSelect])

  return (
    <CarouselContext.Provider
      value={{
        api,
        canScrollNext,
        canScrollPrev,
        carouselRef,
        description,
        opts,
        scrollNext,
        scrollPrev,
        scrollTo,
        selectedIndex,
      }}
    >
      <div
        aria-roledescription={role ? undefined : "carousel"}
        className={cn("relative", className)}
        onKeyDownCapture={handleKeyDown}
        role={role || "region"}
        {...props}
      >
        {!disableSkip && (
          <Button
            className="text-14 border-grey-10 absolute left-1/2 top-8 -z-50 -translate-x-1/2 border p-6 opacity-0 focus:z-50 focus:opacity-100"
            onClick={handleSkipCarousel}
            tabIndex={0}
            text
            underline
            variant="tertiary"
          >
            Skip {description?.toUpperCase() || "Carousel"}
          </Button>
        )}
        {children}
        {!disableSkip && <span ref={skippedRef} tabIndex={-1} />}
      </div>
    </CarouselContext.Provider>
  )
}

Carousel.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  description: PropTypes.string,
  disableSkip: PropTypes.bool,
  opts: PropTypes.object,
  plugins: PropTypes.array,
  role: PropTypes.string,
  setApi: PropTypes.func,
}

const CarouselContent = ({
  className,
  viewportClassName,
  viewportProps,
  ...props
}) => {
  const { carouselRef } = useCarousel()

  return (
    <div
      className={cn("overflow-x-hidden", viewportClassName)}
      ref={carouselRef}
      {...viewportProps}
    >
      <div className={cn("flex", className)} {...props} />
    </div>
  )
}

CarouselContent.propTypes = {
  className: PropTypes.string,
  viewportClassName: PropTypes.string,
  viewportProps: PropTypes.object,
}

const CarouselItem = ({ className, ...props }) => {
  return (
    <div
      aria-roledescription="slide"
      className={cn("min-w-0 shrink-0 grow-0 basis-full", className)}
      role="group"
      {...props}
    />
  )
}

CarouselItem.propTypes = {
  className: PropTypes.string,
}

const CarouselPrevious = ({
  alwaysShow = false,
  arrowClassName,
  className,
  onClick,
  ...props
}) => {
  const { scrollPrev, canScrollPrev } = useCarousel()

  if (!canScrollPrev && !alwaysShow) return null

  return (
    <Button
      className={cn(
        "border-grey-10 desktop:inline-flex disabled:bg-grey-10 absolute left-8 top-1/2 z-20 hidden size-32 -translate-y-1/2 rounded-full border disabled:opacity-50",
        className
      )}
      disabled={!canScrollPrev}
      onClick={() => {
        onClick && onClick()
        scrollPrev()
      }}
      text
      variant="tertiary"
      {...props}
    >
      <FontAwesomeIcon
        className={cn("text-18 text-grey-65", arrowClassName)}
        icon={faChevronLeft}
      />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
}

CarouselPrevious.propTypes = {
  alwaysShow: PropTypes.bool,
  arrowClassName: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
}

const CarouselNext = ({
  alwaysShow = false,
  arrowClassName,
  className,
  onClick,
  ...props
}) => {
  const { scrollNext, canScrollNext } = useCarousel()

  if (!canScrollNext && !alwaysShow) return null

  return (
    <Button
      className={cn(
        "border-grey-10 desktop:inline-flex disabled:bg-grey-10 absolute right-8 top-1/2 z-20 hidden size-32 -translate-y-1/2 rounded-full border disabled:opacity-50",
        className
      )}
      disabled={!canScrollNext}
      onClick={() => {
        onClick && onClick()
        scrollNext()
      }}
      text
      variant="tertiary"
      {...props}
    >
      <FontAwesomeIcon
        className={cn("text-18 text-grey-65", arrowClassName)}
        icon={faChevronRight}
      />
      <span className="sr-only">Next slide</span>
    </Button>
  )
}

CarouselNext.propTypes = {
  alwaysShow: PropTypes.bool,
  arrowClassName: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
}

const CarouselScrollSnaps = ({
  className,
  snapButtonClassName,
  snapDotClassName,
}) => {
  const [scrollSnaps, setScrollSnaps] = useState([])
  const { api, description, scrollTo, selectedIndex } = useCarousel()

  const onReInit = useCallback((api) => {
    if (!api) return
    setScrollSnaps(api.scrollSnapList())
  }, [])

  useEffect(() => {
    if (!api) return
    onReInit(api)
    api.on("reInit", onReInit)
  }, [api, onReInit])

  if (!scrollSnaps?.length) return null

  return (
    <div
      aria-label={`${description || "Carousel"} Navigation`}
      className={cn(
        "absolute -bottom-24 z-20 flex w-full items-center justify-center",
        className
      )}
      role="tablist"
    >
      {scrollSnaps.map((_, index) => (
        <Button
          aria-current={index === selectedIndex ? "true" : undefined}
          aria-label={`Go to slide ${index + 1}`}
          aria-selected={index === selectedIndex}
          className={cn("p-4", snapButtonClassName)}
          key={index}
          onClick={() => scrollTo(index)}
          role="tab"
        >
          <FontAwesomeIcon
            className={cn(
              "text-8",
              index === selectedIndex ? "text-grey-50" : "text-grey-20",
              snapDotClassName
            )}
            icon={faCircle}
          />
        </Button>
      ))}
    </div>
  )
}

CarouselScrollSnaps.propTypes = {
  className: PropTypes.string,
  snapButtonClassName: PropTypes.string,
  snapDotClassName: PropTypes.string,
}

const CarouselScrollBar = ({ className, onChange }) => {
  const [value, setValue] = useState(0)
  const { api } = useCarousel()

  const scrollToProgress = useCallback(
    (progress) => {
      if (!api) return

      const { animation, limit, target, scrollProgress, scrollBody, scrollTo } =
        api.internalEngine()

      animation.stop()

      const currentProgress = scrollProgress.get(target.get())
      const allowedProgress = Math.min(Math.max(progress, 0), 1)
      const progressToTarget = allowedProgress - currentProgress
      const distance = progressToTarget * limit.length * -1

      scrollBody.useDuration(0)
      scrollTo.distance(distance, false)
    },
    [api]
  )

  const onScrollBarChange = useCallback(
    (event) => {
      if (!api) return

      const newProgress = parseFloat(event.target.value)
      setValue(newProgress)
      scrollToProgress(newProgress)
    },
    [api, scrollToProgress]
  )

  useEffect(() => {
    if (!api) return

    setValue(api.scrollProgress())

    api.on("scroll", (api) => setValue(api.scrollProgress()))
  }, [api])

  return (
    <input
      className={cn("carousel-scrollbar", className)}
      max="1"
      min="0"
      onChange={(e) => {
        onChange && onChange(e)
        onScrollBarChange(e)
      }}
      step="0.001"
      type="range"
      value={value}
    />
  )
}

CarouselScrollBar.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
}

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselScrollBar,
  CarouselScrollSnaps,
}
